import { TEMPLATE_TYPES } from '../common/constants';

const SCHEMA_CONTEXT = 'https://schema.org';

// Schema creation functions
const createOrganizationSchema = (seoProps) => ({
  '@type': 'Organization',
  '@id': `${seoProps.origin}/#organization`,
  name: seoProps.websiteName,
  url: seoProps.origin,
  logo: {
    '@type': 'ImageObject',
    url: seoProps.logo 
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      "telephone": seoProps.telephone,
      "email": seoProps.email,
      "contactType": "Customer Service",
      "areaServed": "Worldwide",
      "availableLanguage": ["en"]
    }
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: seoProps.streetAddress,
    addressLocality: seoProps.addressLocality,
    addressRegion: seoProps.addressRegion,
    postalCode: seoProps.postalCode,
    addressCountry: seoProps.addressCountry,
  }
});

const createLodgingBusinessSchema = (seoProps) => ({
  "@type": "LodgingBusiness",
  "name": seoProps.title,
  "url": seoProps.url,
  "image": seoProps.image, 
  "description": seoProps.description,
  "starRating": {
    "@type": "Rating",
    "ratingValue": "5"
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: seoProps.streetAddress,
    addressLocality: seoProps.addressLocality,
    addressRegion: seoProps.addressRegion,
    postalCode: seoProps.postalCode,
    addressCountry: seoProps.addressCountry,
  },
  telephone: seoProps.telephone,
  email: seoProps.email,
});

const createWebsiteSchema = (seoProps) => ({
  '@type': 'WebSite',
  '@id': `${seoProps.origin}/#website`,
  url: seoProps.origin,
  name: seoProps.websiteName,
  publisher: {
    '@id': `${seoProps.origin}/#organization`
  }
});

const createWebPageSchema = (seoProps) => ({
  '@type': 'WebPage',
  '@id': seoProps.url,
  url: seoProps.url,
  name: seoProps.title,
  image: [seoProps.image],
  description: seoProps.description,
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${seoProps.origin}/#website`
  },
  about: {
    '@id': `${seoProps.origin}/#organization`
  },
  breadcrumb: {
    '@id': `${seoProps.url}#breadcrumb`,
  },
});

// Export this function so it can be used independently
export const generateSchema = (seoProps, template) => {
  switch (template) {
    case TEMPLATE_TYPES.HOME: {
      return {
        '@context': SCHEMA_CONTEXT,
        "@graph": [
          {
            '@type': 'WebPage',
            '@id': '',
            isPartOf: {
              '@type': 'WebSite',
              url: seoProps.websiteURL,
              '@id': '/#this-site',
              publisher: { '@id': '/#this-org' },
            },
            publisher: { '@id': '/#this-org' },
          },
          {
           ...createOrganizationSchema(seoProps),
            '@id': '/#this-org'
          }
        ]
      };
    }

    case TEMPLATE_TYPES.PAGE: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@graph': [
           createWebPageSchema(seoProps),
          {
          '@type': 'BreadcrumbList',
          '@id':  `${seoProps.url}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: seoProps.menuLabel,
                item: seoProps.url,
              },
            ]
          },
          createOrganizationSchema(seoProps),
          createWebsiteSchema(seoProps),
        ]
      }
    }

    case TEMPLATE_TYPES.ARTICLE_ARCHIVE: {
      return {
        '@context': SCHEMA_CONTEXT,
        "@graph": [
          createWebPageSchema(seoProps),
          {
            '@type': 'NewsArticle',
            '@id': '',
            headline: seoProps.title,
            image: [seoProps.image],
            datePublished: seoProps.publishDate,
            description: seoProps.description,
            author: {
              '@type': 'Person',
              name: seoProps.author,
            },
            keywords: seoProps.categories,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': seoProps.url,
            },
            publisher: {
              '@type': 'Organization',
              name: seoProps.websiteName,
              url: seoProps.origin,
              logo: {
                '@type': 'ImageObject',
                url: seoProps.logo
              },
            }
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${seoProps.url}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: seoProps.type || 'Articles',
                item: `${seoProps.origin}/${seoProps.breadcrumb}`,
              }
            ],
          }
        ]
      };
    }

    case TEMPLATE_TYPES.ARTICLE: {
      return {
        '@context': SCHEMA_CONTEXT,
         "@graph": [
          {
            '@type': 'NewsArticle',
            '@id': '',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': seoProps.url,
            },
            headline: seoProps.title,
            image: [seoProps.image],
            datePublished: seoProps.publishDate,
            author: {
              '@type': 'Person',
              name: seoProps.author,
            },
            keywords: seoProps.categories,
          },
        {
        '@type': 'BreadcrumbList',
          '@id':  `${seoProps.url}#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: seoProps.origin,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Articles',
              item: `${seoProps.origin}/archive`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: seoProps.title,
              item: seoProps.url,
            },
          ],
        },
      ]
      };
    }

    case TEMPLATE_TYPES.PROJECT: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@graph': [
          createWebPageSchema(seoProps),
          {
          '@type': 'BreadcrumbList',
          '@id':  `${seoProps.url}#breadcrumb`,
            itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Conservation',
                item: `${seoProps.origin}/conservation`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: seoProps.area,
                item: `${seoProps.origin}/${seoProps.areaSlug}`,
              },
              {
                '@type': 'ListItem',
                position: 4,
                name: seoProps.menuLabel,
                item: seoProps.url,
              },
            ],
          },
          createOrganizationSchema(seoProps),
          createWebsiteSchema(seoProps),
        ],
      };
    }

    case TEMPLATE_TYPES.PROMOTION: {
      return {
        '@context': SCHEMA_CONTEXT,
          "@graph": [
          createWebPageSchema(seoProps),
          {
            "@type": "BreadcrumbList",
            "@id": `${seoProps.url}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Promotions',
                item: `${seoProps.origin}/promotions`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: seoProps.title,
                item: seoProps.url,
              },
            ],
          },
          createOrganizationSchema(seoProps),
          createWebsiteSchema(seoProps),
        ]
      };
    }

    case TEMPLATE_TYPES.LODGE: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@graph': [
          createWebPageSchema(seoProps),
          {
            '@type': 'BreadcrumbList',
            '@id':  `${seoProps.url}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Lodges',
                item: `${seoProps.origin}/lodges`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: seoProps.title,
                item: seoProps.url,
              },
            ],
          },
          createOrganizationSchema(seoProps),
          createLodgingBusinessSchema(seoProps),
        ],
      };
    }

    case TEMPLATE_TYPES.LODGE_INFO: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'ItemPage',
        '@graph': [
          {
            '@type': 'Hotel',
            '@id': seoProps.url,
            name: seoProps.title,
            image: [seoProps.image],
            description: seoProps.description,
            "priceRange": "See rates page for current pricing",
            "brand": { "@id": "https://singita.com/#org" },
            "address": {
              '@type': 'PostalAddress',
               addressCountry: seoProps.country,
            },
            "makesOffer": {
              "@type": "Offer",
              "url": seoProps.ratesUrl,
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: seoProps.meta?.latitude,
              longitude: seoProps.meta?.longitude,
            },
            url: seoProps.url,
            telephone: seoProps.meta?.telephone,
          },
          {
            '@type': 'BreadcrumbList',
            '@id':  `${seoProps.url}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: seoProps.origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Lodges',
                item: `${seoProps.origin}/lodges`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: seoProps.title,
                item: seoProps.url,
              },
            ],
          },
        ],
      };
    }

    case TEMPLATE_TYPES.VIDEO: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'VideoObject',
        '@id': seoProps.embedUrl || seoProps.previewUrl,
        name: seoProps.title || seoProps.headline,
        description: seoProps.description || seoProps.caption,
        creator: seoProps.creator,
        uploadDate: seoProps.uploadDate ? new Date(seoProps.uploadDate).toISOString() : undefined,
        embedUrl: seoProps.embedUrl || seoProps.previewUrl,
        thumbnailUrl: seoProps.thumbnailUrl,
        width: seoProps.width,
        height: seoProps.height,
        duration: seoProps.duration,
        publisher: {
          '@type': 'Organization',
          name: seoProps.websiteName,
          url: seoProps.origin,
          logo: {
            '@type': 'ImageObject',
            url: seoProps.logo
          },
        }
      };
    }

    case TEMPLATE_TYPES.AUTHOR: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Person',
        '@id': `${seoProps.url}#Person`,
        name: seoProps.name,
        description: seoProps.description,
        url: seoProps.url,
        jobTitle: seoProps.role,
        image: [
          {
            '@type': 'ImageObject',
            url: seoProps.image,
          },
        ],
      };
    }

    case TEMPLATE_TYPES.EXPERIENCE: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'Thing',
        name: seoProps.title,
        description: seoProps.description,
        location: seoProps.location,
      };
    }

    case TEMPLATE_TYPES.GALLERY: {
      return {
        '@context': SCHEMA_CONTEXT,
        '@type': 'ImageGallery',
        name: seoProps.title,
        location: seoProps.description,
        image: seoProps.image,
        caption: seoProps.altText,
      };
    }

    default:
      return null;
  }
};

// Convenience function specifically for video schema
export const generateVideoSchema = (videoProps) => {
  return generateSchema(videoProps, TEMPLATE_TYPES.VIDEO);
};